import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColumnConfig, numberSort, SortDirections, TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import {
    JobPerformanceDashboardRequest,
    JobPerformanceQuarantine,
    JobPerformanceStuckInQueueJob,
} from "@app/operations/job-performance/jobPerformanceInterfaces";
import { JobPerformanceService } from "@app/operations/job-performance/jobPerformanceService";

const PAGE_SIZE = 10;

type QuarantineRow = {
    [wafflesKeyColumnName]: string;
    client:             string;
    dateFinished:       string;
    dateFinishedRaw:    string;
    dateStarted:        string;
    dateStartedRaw:     string;
    jobs:               number;
    project:            string;
    projectId:          number;
    projectIdFormatted: React.ReactNode;
    publishTime:        string;
    queueWait:          string;
    runTime:            string;
    totalRunTime:       string;
};

type StuckJobRow = {
    [wafflesKeyColumnName]: string;
    client:        string;
    dateQueued:    string;
    dateQueuedRaw: string;
    jobId:         number;
    jobType:       string;
    queue:         string;
    queueWait:     string;
};

type Props = {
    filters: JobPerformanceDashboardRequest;
    refreshToken: number;
};

const toQuarantineRow = (r: JobPerformanceQuarantine): QuarantineRow => ({
    client:             r.clientName,
    dateFinished:       new Date(r.dateRunFinished).toLocaleString(),
    dateFinishedRaw:    String(r.dateRunFinished),
    dateStarted:        new Date(r.dateRunStarted).toLocaleString(),
    dateStartedRaw:     String(r.dateRunStarted),
    jobs:               r.jobsProcessed,
    project:            r.projectName,
    projectId:          r.projectId,
    projectIdFormatted: r.projectId,
    publishTime:        r.publishTimeMinutes.toFixed(1),
    queueWait:          r.queueWaitMinutes.toFixed(1),
    runTime:            r.projectRunTimeMinutes.toFixed(1),
    totalRunTime:       r.totalRunTimeMinutes.toFixed(1),
    [wafflesKeyColumnName]: `${r.clientId}-${r.projectRunConfigurationId}-${r.dateRunStarted}`,
});

const toStuckRow = (r: JobPerformanceStuckInQueueJob): StuckJobRow => ({
    client:        r.clientName,
    dateQueued:    new Date(r.dateQueued).toLocaleString(),
    dateQueuedRaw: String(r.dateQueued),
    jobId:         r.jobId,
    jobType:       r.jobTypeName,
    queue:         r.jobQueueName,
    queueWait:     r.queueWaitMinutes.toFixed(1),
    [wafflesKeyColumnName]: r.jobId.toString(),
});

export const JobPerformanceQuarantineLogTable: FC<Props> = ({ filters, refreshToken }) => {
    const service = useRef(new JobPerformanceService()).current;

    const [quarantineItems, setQuarantineItems]     = useState<JobPerformanceQuarantine[]>([]);
    const [quarantineTotal, setQuarantineTotal]     = useState(0);
    const [quarantineLoading, setQuarantineLoading] = useState(false);
    const [quarantineError, setQuarantineError]     = useState<string | null>(null);
    const [quarantineOpen, setQuarantineOpen]       = useState(true);

    const [stuckItems, setStuckItems]   = useState<JobPerformanceStuckInQueueJob[]>([]);
    const [stuckTotal, setStuckTotal]   = useState(0);
    const [stuckLoading, setStuckLoading] = useState(false);
    const [stuckError, setStuckError]   = useState<string | null>(null);
    const [stuckOpen, setStuckOpen]     = useState(true);

    useEffect(() => {
        setQuarantineLoading(true);
        setQuarantineError(null);
        setQuarantineItems([]);
        setQuarantineTotal(0);
        service.getQuarantineLog({ ...filters, offset: 0, pageSize: PAGE_SIZE })
            .then(result => {
                setQuarantineItems(result.items);
                setQuarantineTotal(result.totalCount);
            })
            .catch(() => setQuarantineError('Failed to load quarantine data. Please refresh and try again.'))
            .finally(() => setQuarantineLoading(false));
    }, [filters]);

    useEffect(() => {
        setStuckLoading(true);
        setStuckError(null);
        setStuckItems([]);
        setStuckTotal(0);
        service.getStuckInQueueJobs({ ...filters, offset: 0, pageSize: PAGE_SIZE })
            .then(result => {
                setStuckItems(result.items);
                setStuckTotal(result.totalCount);
            })
            .catch(() => setStuckError('Failed to load stuck jobs. Please refresh and try again.'))
            .finally(() => setStuckLoading(false));
    }, [filters, refreshToken]);

    const quarantineTableData = useMemo<QuarantineRow[]>(() => quarantineItems.map(toQuarantineRow), [quarantineItems]);
    const stuckTableData      = useMemo<StuckJobRow[]>(() => stuckItems.map(toStuckRow), [stuckItems]);

    const loadMoreQuarantine = useCallback((justChecking: boolean): boolean => {
        if (justChecking) return quarantineItems.length < quarantineTotal;
        service.getQuarantineLog({ ...filters, offset: quarantineItems.length, pageSize: PAGE_SIZE })
            .then(result => {
                setQuarantineItems(prev => [...prev, ...result.items]);
                setQuarantineTotal(result.totalCount);
            })
            .catch(err => console.error('Failed to load more quarantine rows', err));
        return quarantineItems.length + PAGE_SIZE < quarantineTotal;
    }, [quarantineItems.length, quarantineTotal, filters]);

    const loadMoreStuck = useCallback((justChecking: boolean): boolean => {
        if (justChecking) return stuckItems.length < stuckTotal;
        service.getStuckInQueueJobs({ ...filters, offset: stuckItems.length, pageSize: PAGE_SIZE })
            .then(result => {
                setStuckItems(prev => [...prev, ...result.items]);
                setStuckTotal(result.totalCount);
            })
            .catch(err => console.error('Failed to load more stuck jobs', err));
        return stuckItems.length + PAGE_SIZE < stuckTotal;
    }, [stuckItems.length, stuckTotal, filters]);

    const quarantineColumnConfig: ColumnConfig = useMemo(() => ({
        client:             { displayText: 'Client',                                                    sortOrder: 10,  testId: 'quarantine-client' },
        dateFinished:       { displayText: 'Run Finished',                                              sortOrder: 50,  testId: 'quarantine-date-finished' },
        dateFinishedRaw:    { displayText: 'Run Finished (raw)', hidden: true,                                          testId: 'quarantine-date-finished-raw' },
        dateStarted:        { displayText: 'Run Started',                                               sortOrder: 40,  testId: 'quarantine-date-started' },
        dateStartedRaw:     { displayText: 'Run Started (raw)',  hidden: true,                                          testId: 'quarantine-date-started-raw' },
        jobs:               { alignment: TextAlignment.Right, displayText: 'Jobs Processed',    sizeContentToFit: true, sortOrder: 60,  testId: 'quarantine-jobs' },
        project:            { displayText: 'Project Name',                                              sortOrder: 20,  testId: 'quarantine-project' },
        projectId:          { displayText: 'Project ID',            hidden: true,   sizeContentToFit: true,                testId: 'quarantine-project-id' },
        projectIdFormatted: { alignment: TextAlignment.Right, displayText: 'Project ID',        sizeContentToFit: true, sortOrder: 30,  testId: 'quarantine-project-id-formatted' },
        publishTime:        { alignment: TextAlignment.Right, displayText: 'Publish (Min)',      sizeContentToFit: true, sortOrder: 100, testId: 'quarantine-publish-time' },
        queueWait:          { alignment: TextAlignment.Right, displayText: 'Queue Wait (Min)',   sizeContentToFit: true, sortOrder: 80,  testId: 'quarantine-queue-wait' },
        runTime:            { alignment: TextAlignment.Right, displayText: 'Run Time (Min)',     sizeContentToFit: true, sortOrder: 90,  testId: 'quarantine-run-time' },
        totalRunTime:       { alignment: TextAlignment.Right, displayText: 'Total Duration (Min)', sizeContentToFit: true, sortOrder: 70, testId: 'quarantine-total-run-time' },
    }), []);

    const stuckColumnConfig: ColumnConfig = useMemo(() => ({
        client:        { displayText: 'Client',                                                        sortOrder: 10, testId: 'stuck-client' },
        dateQueued:    { displayText: 'Date Queued',                                                   sortOrder: 40, testId: 'stuck-date-queued' },
        dateQueuedRaw: { displayText: 'Date Queued (raw)', hidden: true,                                               testId: 'stuck-date-queued-raw' },
        jobId:         { alignment: TextAlignment.Right, displayText: 'Job ID',  sizeContentToFit: true, sortOrder: 60, testId: 'stuck-job-id' },
        jobType:       { displayText: 'Job Type',                                                      sortOrder: 20, testId: 'stuck-job-type' },
        queue:         { displayText: 'Queue',                                                         sortOrder: 30, testId: 'stuck-queue' },
        queueWait:     { alignment: TextAlignment.Right, displayText: 'Queue Wait (Min)', sizeContentToFit: true, sortOrder: 50, testId: 'stuck-queue-wait' },
    }), []);

    const handleStuckSort = useCallback((segment: StuckJobRow[] | undefined, column: keyof StuckJobRow, direction: SortDirections): StuckJobRow[] => {
        const data = segment !== undefined ? segment : [...stuckTableData];
        const mult = direction === SortDirections.Asc ? 1 : -1;
        if (column === 'queueWait') return numberSort(data, column, mult);
        if (column === 'jobId') return [...data].sort((a, b) => (a.jobId - b.jobId) * mult);
        if (column === 'dateQueued') return [...data].sort((a, b) => (String(a.dateQueuedRaw) > String(b.dateQueuedRaw) ? 1 : -1) * mult);
        return [...data].sort((a, b) => (String(a[column]) > String(b[column]) ? 1 : -1) * mult);
    }, [stuckTableData]);

    const handleQuarantineSort = useCallback((segment: QuarantineRow[] | undefined, column: keyof QuarantineRow, direction: SortDirections): QuarantineRow[] => {
        const data = segment !== undefined ? segment : [...quarantineTableData];
        const mult = direction === SortDirections.Asc ? 1 : -1;
        if (column === 'publishTime' || column === 'queueWait' || column === 'runTime' || column === 'totalRunTime') {
            return numberSort(data, column, mult);
        }
        if (column === 'jobs') return [...data].sort((a, b) => ((a.jobs ?? 0) - (b.jobs ?? 0)) * mult);
        if (column === 'projectIdFormatted' || column === 'projectId') {
            return [...data].sort((a, b) => ((a.projectId ?? 0) - (b.projectId ?? 0)) * mult);
        }
        if (column === 'dateStarted') return [...data].sort((a, b) => (String(a.dateStartedRaw) > String(b.dateStartedRaw) ? 1 : -1) * mult);
        if (column === 'dateFinished') return [...data].sort((a, b) => (String(a.dateFinishedRaw) > String(b.dateFinishedRaw) ? 1 : -1) * mult);
        return [...data].sort((a, b) => (String(a[column]) > String(b[column]) ? 1 : -1) * mult);
    }, [quarantineTableData]);

    return (
        <>
            <div className="jp-subsection">
                <div
                    className="jp-subsection__header"
                    data-testid="job-performance__stuck-in-queue-accordion"
                    onClick={() => setStuckOpen(o => !o)}
                >
                    <span className={`jp-subsection__chevron${stuckOpen ? '' : ' jp-subsection__chevron--closed'}`}>▼</span>
                    <span className="jp-subsection__title">
                        Stuck in Queue ({stuckError ? '—' : stuckTotal})
                    </span>
                </div>
                {stuckOpen && (
                    <div className="jp-subsection__body">
                        {stuckError
                            ? <p className="jp-error-message">{stuckError}</p>
                            : <Waffles<StuckJobRow>
                                columns={stuckColumnConfig}
                                customSort={handleStuckSort}
                                data={stuckTableData}
                                loadMore={loadMoreStuck}
                                loadMoreLabel="See More"
                                loading={stuckLoading}
                                testId="job-performance__stuck-in-queue-table"
                            />
                        }
                    </div>
                )}
            </div>
            <div className="jp-subsection">
                <div
                    className="jp-subsection__header"
                    data-testid="job-performance__quarantine-log-accordion"
                    onClick={() => setQuarantineOpen(o => !o)}
                >
                    <span className={`jp-subsection__chevron${quarantineOpen ? '' : ' jp-subsection__chevron--closed'}`}>▼</span>
                    <span className="jp-subsection__title">
                        Completed Over 24 Hours ({quarantineError ? '—' : quarantineTotal})
                    </span>
                </div>
                {quarantineOpen && (
                    <div className="jp-subsection__body">
                        {quarantineError
                            ? <p className="jp-error-message">{quarantineError}</p>
                            : <Waffles<QuarantineRow>
                                columns={quarantineColumnConfig}
                                customSort={handleQuarantineSort}
                                data={quarantineTableData}
                                loadMore={loadMoreQuarantine}
                                loadMoreLabel="See More"
                                loading={quarantineLoading}
                                testId="job-performance__quarantine-log-table"
                            />
                        }
                    </div>
                )}
            </div>
        </>
    );
};
